/**
 * Shared TTS gating helper.
 *
 * mirror-chat persists `__VYV_INTERNAL_TOOL_ACK__` and `__VYV_INTERNAL_EMPTY__`
 * placeholders to `mirror_messages` to keep the alternating-roles invariant
 * intact for Anthropic's next turn (see supabase/functions/mirror-chat
 * /index.ts:2122-2134). The SSE `final` event sends the ACTUAL cleanedText
 * (often the empty string when only a tool was emitted), but Mini-Mirror
 * voice loops still hit ElevenLabs with the empty payload which returns 400
 * Bad Request. This helper guards every TTS call site uniformly:
 *
 *   - null / undefined         → skip
 *   - empty / whitespace-only  → skip   (root-cause for the AgendaMM 400)
 *   - __VYV_INTERNAL_* prefix  → skip   (defense-in-depth if a future edge
 *                                        function ever leaks the sentinel)
 *
 * Apply at every Mini-Mirror voice loop BEFORE invoking `tts.speak()`.
 */

export const VYV_INTERNAL_SENTINELS = [
  "__VYV_INTERNAL_TOOL_ACK__",
  "__VYV_INTERNAL_EMPTY__",
] as const;

export function shouldSkipTTS(text: string | null | undefined): boolean {
  if (!text) return true;
  const trimmed = text.trim();
  if (trimmed.length === 0) return true;
  if (trimmed.startsWith("__VYV_INTERNAL_")) return true;
  return false;
}

/** True when `text` is one of mirror-chat's internal placeholder sentinels (or
 *  any future `__VYV_INTERNAL_*`). Such content keeps mirror_messages alternating
 *  for Anthropic but must NEVER render as a user-visible chat bubble. */
export function isInternalSentinel(text: string | null | undefined): boolean {
  return typeof text === "string" && text.trim().startsWith("__VYV_INTERNAL_");
}
